import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    keepPreviousData
} from '@tanstack/react-query';
import { BFFAPI } from '@newo/api/gateway';
import type {
    TAttributesParams,
    IPaginatedResponse,
    TPersona,
    ISessionsParams,
    TPersonasParams,
    IConversationsUserPersonaParams,
    IConversationsActsParams,
    TAgentWithFlowsParams,
    TProjectImagesParams,
    TRegistryItemVersionsParams,
    TGroupedRegistryItem,
    TGroupedRegistryItemsParams,
    TRegistryItem
} from '@newo/api/gateway';
import { designerV2API } from '@/services/designer/designerV2Client';
import axios from '@/utils/api/axiosInstance';
import { processDateParams } from '@/modules/DateTime/utils';

export const bffAPI = new BFFAPI(axios);

type TRegistryModule = Awaited<
    ReturnType<typeof designerV2API.listModules>
>[number];

export type TVersionControlRegistryItem =
    TGroupedRegistryItem | TRegistryModule;

export type TVersionControlRegistryVersion = TRegistryItem | TRegistryModule;

type IQueryKeys = {
    getAgentsList: () => [string];
};
export const QUERY_KEYS: IQueryKeys = {
    getAgentsList: () => ['bff/agents/list']
};

// GET Customer Attributes
export const useCustomerAttributesQuery = (params?: TAttributesParams) => {
    return useQuery({
        queryKey: ['bff/customer/attributes', params],
        queryFn: async () => bffAPI.getCustomerAttributes(params),
        placeholderData: keepPreviousData
    });
};

// GET Sessions
export const useSessionsQuery = (params: ISessionsParams) => {
    return useQuery({
        queryKey: ['bff/sessions', params],
        queryFn: async () => {
            const processedParams = processDateParams(params);
            return bffAPI.getSessions(processedParams);
        },
        gcTime: 0,
        staleTime: 0
    });
};

// GET Persona attributes
export const usePersonaAttributesQuery = ({
    personaId,
    params
}: {
    personaId?: string;
    params: TAttributesParams;
}) => {
    return useQuery({
        queryKey: ['bff/persona/attributes', personaId, params],
        queryFn: async () => {
            if (!personaId) {
                throw new Error('Persona ID is required');
            }
            return bffAPI.getPersonaAttributes(personaId, params);
        },
        enabled: Boolean(personaId),
        placeholderData: keepPreviousData
    });
};

// GET Personas
export const usePersonasQuery = (params: TPersonasParams) => {
    return useQuery({
        queryKey: ['bff/personas', params],
        queryFn: async (): Promise<IPaginatedResponse<TPersona>> =>
            bffAPI.getPersonas(params),
        placeholderData: keepPreviousData
    });
};

export const useFindPersonaByName = () =>
    useMutation({
        mutationFn: async (
            name: string
        ): Promise<IPaginatedResponse<TPersona>> =>
            bffAPI.getPersonas({ page: 1, per: 1, name })
    });

// GET Persona
export const usePersonaQuery = (personaId?: string) => {
    return useQuery({
        queryKey: ['bff/persona', personaId],
        queryFn: async (): Promise<TPersona> => {
            if (!personaId) {
                throw new Error('Persona ID is required');
            }
            return bffAPI.getPersona(personaId);
        },
        enabled: Boolean(personaId)
    });
};

// GET Agents List
export const useAgentsListQuery = (params?: TAgentWithFlowsParams) => {
    return useQuery({
        queryKey: ['bff/agents/list', params],
        queryFn: async () => bffAPI.getAgentsList(params),
        enabled: Boolean(params?.project_id)
    });
};

// GET Conversations User Personas
export const usePaginatedConversationsUserPersonasQuery = (
    params: IConversationsUserPersonaParams,
    isEnabled = true
) => {
    return useInfiniteQuery({
        queryKey: ['bff/conversations/user-personas/paginated', params],
        queryFn: async ({ pageParam }) => {
            const processedParams = processDateParams(params);
            return bffAPI.getConversationsUserPersonas({
                ...processedParams,
                page: pageParam
            });
        },
        initialPageParam: 1,
        enabled: isEnabled,
        getNextPageParam: (lastPage) => {
            const { page, per, total } = lastPage.metadata;
            if (page * per >= total) {
                return;
            }
            return page + 1;
        }
    });
};

// GET Conversations User Persona
export const useConversationsUserPersonaQuery = (personaId?: string) => {
    return useQuery({
        queryKey: ['bff/conversations/user-personas', personaId],
        queryFn: async () => {
            if (!personaId) {
                throw new Error('Persona ID is required');
            }
            return bffAPI.getConversationsUserPersona(personaId);
        },
        enabled: Boolean(personaId)
    });
};

// GET Conversations Acts
export const useConversationsActsQuery = (params: IConversationsActsParams) => {
    return useQuery({
        queryKey: ['bff/conversations/acts', params],
        queryFn: async () => {
            const processedParams = processDateParams(params);
            return bffAPI.getConversationsActs(processedParams);
        },
        refetchOnReconnect: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false
    });
};

export const usePaginatedConversationsActsQuery = (
    params: IConversationsActsParams,
    isEnabled = true
) => {
    return useInfiniteQuery({
        queryKey: ['bff/conversations/acts/paginated', params],
        queryFn: async ({ pageParam }) => {
            const processedParams = processDateParams(params);
            return bffAPI.getConversationsActs({
                ...processedParams,
                page: pageParam
            });
        },
        initialPageParam: 1,
        enabled: isEnabled,
        getNextPageParam: (lastPage) => {
            const { page, per, total } = lastPage.metadata;
            if (page * per >= total) {
                return;
            }
            return page + 1;
        }
    });
};

// GET Conversations connectors
export const useConversationsConnectorsQuery = () => {
    return useQuery({
        queryKey: ['bff/conversations/connectors'],
        queryFn: async () => bffAPI.getConversationsConnectors()
    });
};

// STORAGES
export const useStorageImagesQuery = (
    storageId: string,
    params: TProjectImagesParams
) => {
    return useQuery({
        queryKey: ['storage/images', storageId, params],
        queryFn: async () => bffAPI.getStorageImages(storageId, params)
    });
};

// Registry Items
export const useRegistryItemVersionsQuery = (
    registryIdn?: string | null,
    registryItemIdn?: string | null,
    params?: TRegistryItemVersionsParams
) => {
    return useQuery({
        queryKey: [
            'registry/items/versions',
            registryIdn,
            registryItemIdn,
            params
        ],
        queryFn: async () => {
            if (!registryIdn || !registryItemIdn || !params) {
                throw new Error(
                    'Registry ID and Registry Item ID are required'
                );
            }

            return bffAPI.getRegistryItemVersions(
                registryIdn,
                registryItemIdn,
                params
            );
        },
        enabled: Boolean(registryIdn && registryItemIdn)
    });
};

export const useRegistryItemsGroupedByIdnQuery = (
    registryId?: string | null,
    params?: TGroupedRegistryItemsParams,
    options?: { isProjectsV2Enabled?: boolean }
) => {
    return useQuery({
        queryKey: [
            'registry/items/grouped',
            registryId,
            options?.isProjectsV2Enabled ? undefined : params,
            options?.isProjectsV2Enabled
        ],
        queryFn: async () => {
            if (!registryId) {
                throw new Error('Registry ID is required');
            }

            if (options?.isProjectsV2Enabled) {
                return designerV2API.listModules(registryId);
            }

            if (!params) {
                throw new Error('Registry ID is required');
            }

            return bffAPI.getRegistryItemsGroupedByIdn(registryId, params);
        }
    });
};

export const useRegistryItemVersionsInfiniteQuery = (
    registryIdn: string,
    registryItemIdn: string,
    params: TRegistryItemVersionsParams,
    options?: { isProjectsV2Enabled?: boolean }
) => {
    return useInfiniteQuery({
        queryKey: [
            'registry/items/versions',
            registryIdn,
            registryItemIdn,
            options?.isProjectsV2Enabled
                ? { page: params.page, per: params.per }
                : params,
            options?.isProjectsV2Enabled
        ],
        queryFn: async ({ pageParam }) => {
            if (options?.isProjectsV2Enabled) {
                return designerV2API.listModuleVersions(
                    registryIdn,
                    registryItemIdn,
                    { page: pageParam, per: params.per }
                );
            }

            return bffAPI.getRegistryItemVersions(
                registryIdn,
                registryItemIdn,
                { ...params, page: pageParam }
            );
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const { page, per, total } = lastPage.metadata;
            if (page * per >= total) {
                return;
            }
            return page + 1;
        }
    });
};

// PROJECT ATTRIBUTES
export const useProjectAttributesQuery = (
    projectId?: string,
    params?: TAttributesParams
) => {
    return useQuery({
        queryKey: ['project/attributes', projectId, params],
        queryFn: async () => {
            if (!projectId || !params) {
                throw new Error('Project ID is required');
            }
            return bffAPI.getProjectAttributes(projectId, params);
        }
    });
};

export const useWidgetsQuery = (params: {
    widget_type: 'internal' | 'external';
}) => {
    return useQuery({
        queryKey: ['widgets', params],
        queryFn: async () => bffAPI.getWidgets(params)
    });
};
